import { useCallback, useContext, useEffect, useRef } from "react";

import { useRoleContext } from "./useRoleContext";
import {
  DEFAULT_CARRIER_SHIPMENT_STATUSES,
  DEFAULT_SHIPMENT_STATUSES,
  DEFAULT_SUPPLIER_SHIPMENT_STATUSES,
} from "../components/Shipper/components/ShipmentsFilter/constants";
import { shipmentFiltersContext } from "../context";

export function useShipmentFiltersContext(
  tabType: "regular" | "supplier" = "regular",
) {
  const shipmentFiltersCtx = useContext(shipmentFiltersContext);
  const { dispatch, refreshCallbacks } = shipmentFiltersCtx;
  const { role } = useRoleContext();
  const previousRoleRef = useRef<null | Role>(null);

  useEffect(() => {
    if (previousRoleRef.current !== role) {
      previousRoleRef.current = role;

      if (role?.type === "CARRIER") {
        dispatch({
          type: "setShipmentStatus",
          status: DEFAULT_CARRIER_SHIPMENT_STATUSES,
          tabType,
        });
        if (!shipmentFiltersCtx.regularFilters?.shipmentRelationship) {
          shipmentFiltersCtx.dispatch({
            type: "setShipmentRelationship",
            shipmentRelation: "partner",
            tabType: "regular",
          });
        }
      } else if (role?.type === "SHIPPER") {
        if (
          tabType === "supplier" &&
          !shipmentFiltersCtx.supplierFilters?.status
        ) {
          dispatch({
            type: "setShipmentStatus",
            status: DEFAULT_SUPPLIER_SHIPMENT_STATUSES,
            tabType,
          });
        } else if (
          tabType === "regular" &&
          !shipmentFiltersCtx.regularFilters?.status
        ) {
          dispatch({
            type: "setShipmentStatus",
            status: DEFAULT_SHIPMENT_STATUSES,
            tabType,
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, dispatch, tabType]);

  const updateOrigin = (
    locationKey: keyof ShipmentLocationFilter,
    locationValue: string[],
  ) => {
    dispatch({
      type: "setOrigin",
      value: locationValue,
      originKey: locationKey,
      tabType,
    });
  };

  const updateDestination = (
    locationKey: keyof ShipmentLocationFilter,
    locationValue: string[],
  ) => {
    dispatch({
      type: "setDestination",
      value: locationValue,
      destinationKey: locationKey,
      tabType,
    });
  };

  const updateOrderBy = (orderBy: string) => {
    dispatch({ type: "setOrderBy", orderBy, tabType });
  };

  const updateEquipmentType = (equipmentTypes: string[]) => {
    dispatch({ type: "setEquipmentType", equipments: equipmentTypes, tabType });
  };

  const updateServiceType = (serviceTypes: string[]) => {
    dispatch({ type: "setService", services: serviceTypes, tabType });
  };

  const updateShipmentStatus = (status: ShipmentStatusValue[] | undefined) => {
    dispatch({ type: "setShipmentStatus", status, tabType });
  };

  const updateCommodity = (commodity: Commodity | null) => {
    dispatch({ type: "setCommodity", commodity, tabType });
  };

  const clearFilters = () => {
    dispatch({ type: "clearShipmentFilters", tabType });
  };

  const setFilters = (filters: ShipmentFilters) => {
    dispatch({ type: "setShipmentFilters", filters, tabType });
  };

  const updateShipmentVisibility = (visibility: ShipmentVisibility) => {
    dispatch({ type: "setShipmentVisibility", visibility, tabType });
  };

  const updateShipmentRelation = (shipmentRelation: ShipmentRelation) => {
    dispatch({ type: "setShipmentRelationship", shipmentRelation, tabType });
  };

  const updateShipmentName = (shipmentName: string) => {
    dispatch({ type: "setShipmentName", shipmentName, tabType });
  };

  const updateShipmentType = (shipmentTypes: string[]) => {
    dispatch({ type: "setShipmentTypeAction", shipmentTypes, tabType });
  };

  const updateIncludeCompanyShipments = (includeCompanyShipments: boolean) => {
    dispatch({
      type: "SetIncludeCompanyShipments",
      value: includeCompanyShipments,
      tabType,
    });
  };

  const updateIncludeBranchShipments = (setIncludeBranchShipments: boolean) => {
    dispatch({
      type: "SetIncludeBranchShipments",
      value: setIncludeBranchShipments,
      tabType,
    });
  };

  const updateBranches = (branchIds: string[]) => {
    dispatch({ type: "setBranches", branches: branchIds, tabType });
  };

  const updateShippers = (shipperIds: string[]) => {
    dispatch({ type: "setShippers", shippers: shipperIds, tabType });
  };

  const updateShipperBusinessType = (
    businessTypes: BusinessTypeValue[] | null,
  ) => {
    dispatch({
      type: "setShipperBusinessType",
      shipperBusinessType: businessTypes,
    });
  };

  const registerRefreshCallback = useCallback(
    (callback: () => void) => {
      dispatch({
        type: "registerRefreshCallback",
        callback,
        tabType,
      });
    },
    [dispatch, tabType],
  );

  const unregisterRefreshCallback = useCallback(() => {
    dispatch({
      type: "unregisterRefreshCallback",
      tabType,
    });
  }, [dispatch, tabType]);

  const refreshShipments = useCallback(() => {
    const callback =
      tabType === "supplier"
        ? refreshCallbacks.supplier
        : refreshCallbacks.regular;

    if (callback) {
      callback();
    }
  }, [refreshCallbacks, tabType]);

  // Return the appropriate filter state based on tab type
  const currentFilters =
    tabType === "supplier"
      ? shipmentFiltersCtx.supplierFilters
      : shipmentFiltersCtx.regularFilters;

  return {
    filters: currentFilters,
    updateOrigin,
    updateDestination,
    updateOrderBy,
    updateEquipmentType,
    updateServiceType,
    updateShipmentStatus,
    updateCommodity,
    clearFilters,
    setFilters,
    updateShipmentVisibility,
    updateShipmentRelation,
    updateShipmentName,
    updateShipmentType,
    updateIncludeCompanyShipments,
    updateIncludeBranchShipments,
    updateBranches,
    updateShippers,
    updateShipperBusinessType,
    registerRefreshCallback,
    unregisterRefreshCallback,
    refreshShipments,
  };
}
